import {
    SEARCH_ENTRY_POINT_CLASSIC_HANDOFF,
    SEARCH_ENTRY_POINT_DEEP_LINK,
    SEARCH_ENTRY_POINT_SEARCH_FORM,
    SEARCH_TERM_MAX_LENGTH,
} from './constants';

// The results routes are `/:direction(depart|return)/...`, behind an optional
// locale segment.
const RESULTS_PATH = /(^|\/)(depart|return)\//;

/** The city a pick lies in, never the pick as the traveler typed it. */
interface SearchedCity {
    cityName?: string;
    region?: string;
}

interface AddressComponent {
    long_name?: string;
    short_name?: string;
    types?: string[];
}

function findComponent(components: AddressComponent[], type: string): AddressComponent | undefined {
    return components.find((component) => component.types?.includes(type));
}

/**
 * The city a picked Google place lies in, as `{ cityName, region }` -- the
 * region the trips store uses (`regionFromStateAndCountry`): the state for the
 * US and Canada, the country elsewhere.
 *
 * Only the locality is read -- or, where Google gives none, the postal town
 * (UK) or the first-level sublocality (New York's outer boroughs: a Brooklyn
 * address has `Brooklyn` there and no locality at all) -- so a street address
 * or a station reduces to its city and the street never leaves this function.
 * No city, no result.
 */
function cityFromAddressComponents(
    components?: AddressComponent[],
): { cityName: string; region?: string } | undefined {
    if (!components?.length) {
        return undefined;
    }

    const city = findComponent(components, 'locality')
        || findComponent(components, 'postal_town')
        || findComponent(components, 'sublocality_level_1');

    if (!city?.long_name) {
        return undefined;
    }

    const country = findComponent(components, 'country')?.short_name;
    const state = findComponent(components, 'administrative_area_level_1')?.short_name;
    const region = country && ['US', 'CA'].includes(country.toUpperCase())
        ? state || country
        : country;

    return region
        ? { cityName: city.long_name, region }
        : { cityName: city.long_name };
}

function formatCity(city?: SearchedCity): string | undefined {
    if (!city?.cityName) {
        return undefined;
    }

    return city.region
        ? `${city.cityName}, ${city.region}`
        : city.cityName;
}

/**
 * `{city}, {region} to {city}, {region}`, from the resolved cities only.
 *
 * Never built from the autocomplete pick: the widget accepts street addresses
 * and stations, and a home address is personal data that must not reach GA4 or
 * TikTok. So when either end did not resolve to a city there is no term at all,
 * rather than a fallback to the input. No dates either -- they would push an
 * already high-cardinality dimension into GA4's "(other)" bucket.
 */
function buildSearchTerm(
    departCity?: SearchedCity,
    arriveCity?: SearchedCity,
): string | undefined {
    const depart = formatCity(departCity);
    const arrive = formatCity(arriveCity);

    if (!depart || !arrive) {
        return undefined;
    }

    return `${depart} to ${arrive}`.slice(0, SEARCH_TERM_MAX_LENGTH);
}

function parseUrl(url?: string): URL | undefined {
    if (!url) {
        return undefined;
    }

    try {
        return new URL(url);
    } catch {
        return undefined;
    }
}

function isWanderuHost(hostname: string): boolean {
    return hostname === 'wanderu.com' || hostname.endsWith('.wanderu.com');
}

/**
 * Where a search the page had no in-app signal for came from.
 *
 * Only the search the page landed on can be a deep link: every later search on
 * the same page was started from something on it, and so was the first one when
 * the page landed anywhere but the results (the ui-react homepage routes to the
 * results client-side, so its referrer is whatever brought the traveler to the
 * homepage). For the landing search the referrer decides: another Wanderu page
 * -- nexus's widget hands off with a full page load -- is the form; the explore
 * app and labs are the classic handoff; anything else, or nothing, is a deep
 * link.
 *
 * A date suggestion is an in-app signal and is not decided here; the caller
 * marks it when the suggestion is clicked.
 */
function resolveSearchEntryPoint({
    isFirstSearch,
    landingUrl,
    referrer,
}: {
    isFirstSearch: boolean;
    landingUrl?: string;
    referrer?: string;
}): string {
    if (!isFirstSearch) {
        return SEARCH_ENTRY_POINT_SEARCH_FORM;
    }

    const landing = parseUrl(landingUrl);

    if (landing && !RESULTS_PATH.test(landing.pathname)) {
        return SEARCH_ENTRY_POINT_SEARCH_FORM;
    }

    const from = parseUrl(referrer);

    if (!from || !isWanderuHost(from.hostname)) {
        return SEARCH_ENTRY_POINT_DEEP_LINK;
    }

    if (from.hostname.startsWith('labs.') || /(^|\/)explore(\/|$)/.test(from.pathname)) {
        return SEARCH_ENTRY_POINT_CLASSIC_HANDOFF;
    }

    return SEARCH_ENTRY_POINT_SEARCH_FORM;
}

export type { AddressComponent, SearchedCity };
export {
    buildSearchTerm,
    cityFromAddressComponents,
    resolveSearchEntryPoint,
};
